import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Plus, Key, User, UserMinus, Lock, Mail, Pause, Play, Palette, Eye, Users, RefreshCw, Users2, CheckCircle2, Sparkles, Building2, Settings, FileText, Download, Shield, AlertTriangle, Clock, CheckCircle, XCircle, GitBranch, Inbox, ShieldCheck } from 'lucide-react';
import PartnerNavBar from '../../components/PartnerNavBar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuthenticatedFetch } from '../../hooks/useApi';
import './styles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';

export default function PartnerDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();

  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [pendingLegalReviews, setPendingLegalReviews] = useState(0);
  const [offlineContinuityStats, setOfflineContinuityStats] = useState({ clear: 0, atRisk: 0, incomplete: 0 });
  const [continuityOverview, setContinuityOverview] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchPendingLegalReviews();
    fetchOfflineContinuityStats();
    fetchContinuityOverview();
  }, []);

  const fetchPendingLegalReviews = async () => {
    try {
      const response = await authenticatedFetch('/api/partner/legal-pending');
      if (response && response.documents) {
        setPendingLegalReviews(response.documents.length || 0);
      }
    } catch (err) {
      console.error('Error fetching pending legal reviews:', err);
      setPendingLegalReviews(0);
    }
  };

  const fetchOfflineContinuityStats = async () => {
    try {
      // Get all families
      const familiesData = await authenticatedFetch('/api/partner/families');
      const families = familiesData.families || [];

      // Fetch offline continuity status for each family
      const statusPromises = families.map(family =>
        authenticatedFetch(`/api/governance/offline-continuity/${family._id}`)
          .then(data => ({ familyId: family._id, status: data.status?.status }))
          .catch(() => ({ familyId: family._id, status: null }))
      );

      const statuses = await Promise.all(statusPromises);
      const stats = {
        clear: statuses.filter(s => s.status === 'CLEAR').length,
        atRisk: statuses.filter(s => s.status === 'AT_RISK').length,
        incomplete: statuses.filter(s => s.status === 'INCOMPLETE' || !s.status).length
      };

      setOfflineContinuityStats(stats);
    } catch (err) {
      console.error('Error fetching offline continuity stats:', err);
    }
  };

  const fetchContinuityOverview = async () => {
    try {
      const data = await authenticatedFetch('/api/partner/families/continuity-overview');
      if (data && data.continuity) {
        setContinuityOverview(data.continuity);
      }
    } catch (err) {
      console.error('Error fetching continuity overview:', err);
      // Don't fail silently - just don't show the section
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch partner info, stats, and activity in parallel
      const [partnerData, statsData, activityData] = await Promise.all([
        authenticatedFetch('/api/partner/me'),
        authenticatedFetch('/api/partner/stats'),
        authenticatedFetch('/api/partner/activity?limit=10')
      ]);

      setPartner(partnerData.partner);
      setStats(statsData.stats);
      setActivities(activityData.activities || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <PartnerNavBar />
        <LoadingSpinner type="page" message="Loading dashboard..." />
      </>
    );
  }

  return (
    <div className="partner-dashboard">
      <PartnerNavBar />

      <div className="partner-dashboard-container">
        <div className="partner-dashboard-header">
          <h1>Partner Dashboard</h1>
          <p>Welcome back, {partner?.name || 'Partner'}</p>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <Users2 size={32} className="stat-icon-svg" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.familiesManaged || 0}</div>
              <div className="stat-label">Families Managed</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle2 size={32} className="stat-icon-svg" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.activeVaults || 0}</div>
              <div className="stat-label">Active Vaults</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Sparkles size={32} className="stat-icon-svg" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats?.newThisWeek || 0}</div>
              <div className="stat-label">New This Week</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <Download size={32} className="stat-icon-svg" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{offlineContinuityStats.clear}</div>
              <div className="stat-label">Offline Continuity Ready</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <h2>Quick Actions</h2>
          <div className="quick-actions-grid">
            <button
              className="action-card"
              onClick={() => navigate('/partner/families/create')}
            >
              <div className="action-icon">
                <Plus size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Create New Family Vault</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/families')}
            >
              <div className="action-icon">
                <Users2 size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">View All Families</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/branding')}
            >
              <div className="action-icon">
                <Palette size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Branding & Customization</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/communications')}
            >
              <div className="action-icon">
                <Mail size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Client Communications</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/reports')}
            >
              <div className="action-icon">
                <FileText size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Continuity Reports</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/succession')}
            >
              <div className="action-icon">
                <GitBranch size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Succession</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/emergency-approvals?type=advisor')}
              title="Review and approve emergency access requests"
            >
              <div className="action-icon">
                <ShieldCheck size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Emergency Approvals</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/account')}
            >
              <div className="action-icon">
                <Settings size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Partner Settings</div>
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/legal-document-review')}
            >
              <div className="action-icon">
                <FileText size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Review Legal Documents</div>
              {pendingLegalReviews > 0 && (
                <div className="action-badge" style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: '#9C27B0',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {pendingLegalReviews}
                </div>
              )}
            </button>

            <button
              className="action-card"
              onClick={() => navigate('/partner/families')}
              title="Review documents received from client families"
            >
              <div className="action-icon">
                <Inbox size={32} className="action-icon-svg" />
              </div>
              <div className="action-title">Document Review</div>
            </button>
          </div>
        </div>

        {/* Continuity Readiness Overview */}
        {continuityOverview && (
          <div className="continuity-readiness-section">
            <h2>Continuity Readiness Overview</h2>
            <p className="section-subtitle">Presence and absence indicators across all client families</p>

            <div className="continuity-stats-grid">
              {Object.entries(continuityOverview).map(([familyId, data]) => {
                // Handle both direct data and nested continuity object
                const continuityData = data?.continuity || data;
                if (!continuityData || !continuityData.continuityState) return null;

                const state = continuityData.continuityState.toUpperCase();
                const stateConfig = {
                  'CLEAR': { label: 'Clear', color: '#4CAF50', icon: CheckCircle },
                  'STABLE': { label: 'Stable', color: '#4CAF50', icon: CheckCircle },
                  'AT_RISK': { label: 'At Risk', color: '#FF9800', icon: AlertTriangle },
                  'ATRISK': { label: 'At Risk', color: '#FF9800', icon: AlertTriangle },
                  'INCOMPLETE': { label: 'Incomplete', color: '#F44336', icon: XCircle },
                  'PARTIAL': { label: 'Partial', color: '#FF9800', icon: Clock },
                  'UNKNOWN': { label: 'Unknown', color: '#9E9E9E', icon: Clock }
                };

                const config = stateConfig[state] || stateConfig['UNKNOWN'];
                const StateIcon = config.icon;

                return (
                  <div key={familyId} className="continuity-family-card">
                    <div className="continuity-family-header">
                      <StateIcon size={20} style={{ color: config.color }} />
                      <span className="continuity-family-name">
                        {data?.familyName || continuityData?.familyName || 'Family'}
                      </span>
                      <span className="continuity-state" style={{ color: config.color }}>
                        {config.label}
                      </span>
                    </div>
                    <div className="continuity-indicators">
                      <div className="indicator-item">
                        <span className="indicator-label">Authority:</span>
                        <span className={`indicator-value ${continuityData.currentAuthority ? 'present' : 'absent'}`}>
                          {continuityData.currentAuthority ? 'Present' : 'Absent'}
                        </span>
                      </div>
                      <div className="indicator-item">
                        <span className="indicator-label">Emergency Access:</span>
                        <span className={`indicator-value ${continuityData.emergencyTierConfig?.tier1Configured ? 'present' : 'absent'}`}>
                          {continuityData.emergencyTierConfig?.tier1Configured ? 'Configured' : 'Not Configured'}
                        </span>
                      </div>
                      <div className="indicator-item">
                        <span className="indicator-label">Guardians:</span>
                        <span className={`indicator-value ${continuityData.offlineContinuity?.guardianCount >= 3 ? 'present' : 'absent'}`}>
                          {continuityData.offlineContinuity?.guardianCount || 0}/3
                        </span>
                      </div>
                      <div className="indicator-item">
                        <span className="indicator-label">Unresolved Items:</span>
                        <span className={`indicator-value ${continuityData.unresolvedContinuityItemsCount === 0 ? 'present' : 'absent'}`}>
                          {continuityData.unresolvedContinuityItemsCount || 0}
                        </span>
                      </div>
                    </div>
                    {continuityData.lastGovernanceConfirmedAt && (
                      <div className="continuity-footer">
                        <Clock size={14} />
                        <span>Last check: {formatTimeAgo(continuityData.lastGovernanceConfirmedAt)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {Object.keys(continuityOverview).length === 0 && (
              <div className="empty-continuity-state">
                <Shield size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                <p>No continuity data available yet</p>
              </div>
            )}
          </div>
        )}

        {/* Recent Activity */}
        {activities.length > 0 && (
          <div className="recent-activity-section">
            <h2>Recent Activity</h2>
            <div className="activity-list">
              {activities.map((activity) => (
                <div key={activity._id} className="activity-item">
                  <div className="activity-icon">
                    {getActivityIcon(activity.action)}
                  </div>
                  <div className="activity-content">
                    <div className="activity-action">
                      {formatActivityAction(activity.action)}
                      {activity.memberName && (
                        <span className="activity-member"> for {activity.memberName}</span>
                      )}
                      {activity.familyName && (
                        <span className="activity-family"> • {activity.familyName}</span>
                      )}
                    </div>
                    <div className="activity-time">
                      {formatTimeAgo(activity.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getActivityIcon(action) {
  const iconProps = { size: 18, className: 'activity-icon-svg' };
  const icons = {
    created_family: <Plus {...iconProps} />,
    updated_family: <RefreshCw {...iconProps} />,
    reset_passcode: <Key {...iconProps} />,
    reset_password: <Key {...iconProps} />,
    added_member: <User {...iconProps} />,
    member_added_by_admin: <User {...iconProps} />,
    removed_member: <UserMinus {...iconProps} />,
    changed_passcode: <Lock {...iconProps} />,
    sent_onboarding_email: <Mail {...iconProps} />,
    disabled_vault: <Pause {...iconProps} />,
    enabled_vault: <Play {...iconProps} />,
    updated_branding: <Palette {...iconProps} />,
    viewed_family: <Eye {...iconProps} />
  };
  return icons[action] || <Users {...iconProps} />;
}

function formatActivityAction(action) {
  const actions = {
    created_family: 'Created family vault',
    updated_family: 'Updated family',
    reset_passcode: 'Reset passcode',
    reset_password: 'Reset password',
    added_member: 'Added member',
    member_added_by_admin: 'Member added by admin',
    removed_member: 'Removed member',
    changed_passcode: 'Changed passcode',
    sent_onboarding_email: 'Sent onboarding email',
    disabled_vault: 'Disabled vault',
    enabled_vault: 'Enabled vault',
    updated_branding: 'Updated branding',
    viewed_family: 'Viewed family'
  };
  return actions[action] || action;
}

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

